import { PosBffApi } from '@kinderlabs-pos/apis/pos';
import { OrderState } from '@kinderlabs-pos/state';
import {
	FullSizeCircularProgress,
	InfoPrintStackRow,
	RotatingRefreshButton,
	SearchBox,
} from '@kinderlabs-pos/ui-components';
import { Button, DialogActions, DialogContent, Stack, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { useAtomValue } from 'jotai';
import { useEffect, useState } from 'react';
import { HandleSubmitBillingKeyPaymentArgsType } from './BillingKeyDialog';

export const BillingKeyDialogContent = ({
	closeDialog,
	submitPayment,
	goodsName,
}: {
	closeDialog?: () => void;
	submitPayment: (args: HandleSubmitBillingKeyPaymentArgsType) => Promise<void>;
	goodsName: string;
}) => {
	const [ddiziId, setDdiziId] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);

	const { data, isLoading, refetch } = useQuery({
		queryKey: ['billing-key', ddiziId],
		queryFn: async () => {
			if (!ddiziId) return null;
			try {
				return await PosBffApi.scanBillingKey({ ddiziId: ddiziId });
			} catch (e) {
				//@ts-ignore
				setErrorMessage(e.message);
				return null;
			}
		},
		suspense: false,
	});
	const cardBilling =
		data && data.billKey && data.cardBillingTemplate
			? {
					billKey: data.billKey,
					memberId: data.memberId,
					cardBillingTemplate: data.cardBillingTemplate,
					leftUsableAmount: data.leftUsableAmount,
			  }
			: null;

	// 2. Payment
	const { remainingPay } = useAtomValue(OrderState.aggregate.payments);

	// 3. Formik
	const { handleSubmit, handleChange, isSubmitting, values, setValues, errors } = useFormik<{
		method: 'VAUNCE_APP_BILLING';
		amount: number;
		submit: string | null;
	}>({
		initialValues: {
			method: 'VAUNCE_APP_BILLING',
			amount: remainingPay,
			submit: null,
		},
		onSubmit: async (values, { setErrors, resetForm }) => {
			try {
				if (cardBilling && ddiziId) {
					await submitPayment({
						method: values.method,
						amount: values.amount,
						ddiziId: ddiziId,
						billKey: cardBilling.billKey,
						goodsName,
						memberId: cardBilling.memberId,
					});

					closeDialog && closeDialog();

					resetForm();
				}
			} catch (e) {
				//@ts-ignore
				setErrors({ submit: e.message });
				closeDialog && closeDialog();
			}
		},
	});

	const 결제가능 =
		data &&
		data.personalInfoUseAgree &&
		data.fromVaunceToCardPersonalInfoUseAgree &&
		data.fromCardToVauncePersonalInfoUseAgree &&
		data.cardBillingTemplate.isDdiziPaymentAllowed &&
		data.cardBillingTemplate.maxUsageForOneDay >= remainingPay &&
		data.leftUsableAmount >= remainingPay;

	return (
		<>
			<DialogContent sx={{ width: 520, height: 370, mb: 2 }}>
				<Stack>
					<Stack
						width={'100%'}
						spacing={1}
						direction={'row'}>
						<RotatingRefreshButton
							handleClick={() => {
								refetch();
							}}
						/>
						<SearchBoxHere
							keyword={ddiziId ?? ''}
							setSearch={setDdiziId}
						/>
					</Stack>
					<Typography
						variant={'body2'}
						textAlign={'center'}>
						{'바운스앱 결제키 검색을 위해 티켓을 태그해주세요'}
					</Typography>
					<Stack
						sx={{ gap: 4 }}
						width={'100%'}>
						{isLoading ? (
							<FullSizeCircularProgress />
						) : (
							<>
								{cardBilling ? (
									<>
										{결제가능 ? (
											<Stack sx={{ py: 4, gap: 1 }}>
												<Typography sx={{ color: 'green' }}>{'결제 가능'}</Typography>
												<InfoPrintStackRow
													label={'1일 결제 한도'}
													value={
														<Typography>
															{cardBilling.cardBillingTemplate.maxUsageForOneDay.toLocaleString()}
														</Typography>
													}
												/>
												<Stack>
													<InfoPrintStackRow
														label={'남은 결제 가능 금액'}
														value={
															<Typography>
																{cardBilling.leftUsableAmount.toLocaleString()}
															</Typography>
														}
													/>
													<InfoPrintStackRow
														label={'이번 주문에서 결제할 금액'}
														value={<Typography>{remainingPay.toLocaleString()}</Typography>}
													/>
												</Stack>
												<InfoPrintStackRow
													label={'결제시 예상 잔액'}
													value={
														<Typography>
															{(cardBilling.leftUsableAmount - remainingPay).toLocaleString()}
														</Typography>
													}
												/>
											</Stack>
										) : (
											<Stack sx={{ py: 4, gap: 1 }}>
												<Typography color={'error'}>{'결제 불가능'}</Typography>
												<InfoPrintStackRow
													label={'개인정보이용동의'}
													value={
														<Typography color={data?.personalInfoUseAgree ? '' : 'error'}>
															{data?.personalInfoUseAgree ? '동의' : '미동의'}
														</Typography>
													}
												/>
												<InfoPrintStackRow
													label={'카드사 => 바운스앱 개인정보 이용동의'}
													value={
														<Typography
															color={data?.fromCardToVauncePersonalInfoUseAgree ? '' : 'error'}>
															{data?.fromCardToVauncePersonalInfoUseAgree ? '동의' : '미동의'}
														</Typography>
													}
												/>
												<InfoPrintStackRow
													label={'바운스앱 => 카드사 개인정보 이용동의'}
													value={
														<Typography
															color={data?.fromVaunceToCardPersonalInfoUseAgree ? '' : 'error'}>
															{data?.fromVaunceToCardPersonalInfoUseAgree ? '동의' : '미동의'}
														</Typography>
													}
												/>
												<InfoPrintStackRow
													label={'바운스앱 결제 가능 여부'}
													value={
														<Typography
															color={
																cardBilling.cardBillingTemplate.isDdiziPaymentAllowed ? '' : 'error'
															}>
															{cardBilling.cardBillingTemplate.isDdiziPaymentAllowed
																? '가능'
																: '불가능'}
														</Typography>
													}
												/>
												<InfoPrintStackRow
													label={'하루 결제 한도'}
													value={
														<Typography>
															{cardBilling.cardBillingTemplate.maxUsageForOneDay.toLocaleString()}
														</Typography>
													}
												/>
												<InfoPrintStackRow
													label={'남은 결제 가능 금액'}
													value={
														<Typography>{cardBilling.leftUsableAmount.toLocaleString()}</Typography>
													}
												/>
												{remainingPay > cardBilling.leftUsableAmount && (
													<InfoPrintStackRow
														label={
															<Typography color='error'>
																{'남은 결제가능 금액이 부족합니다.'}
															</Typography>
														}
														value={
															<Typography color='error'>
																{(cardBilling.leftUsableAmount - remainingPay).toLocaleString()}
															</Typography>
														}
													/>
												)}
											</Stack>
										)}
									</>
								) : (
									<Stack
										sx={{ py: 4, flex: 1 }}
										alignItems={'center'}>
										<Typography>
											{errorMessage ?? '등록된 방문등록, 결제 카드가 없습니다.'}
										</Typography>
									</Stack>
								)}
							</>
						)}
					</Stack>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Stack
					direction={'row'}
					spacing={1}
					alignItems={'center'}>
					<Button
						disabled={!결제가능 || isSubmitting}
						onClick={() => handleSubmit()}
						variant={'contained'}>
						확인
					</Button>
					<Button
						onClick={closeDialog}
						variant={'outlined'}>
						취소
					</Button>
				</Stack>
			</DialogActions>
		</>
	);
};

const SearchBoxHere = ({
	keyword,
	setSearch,
}: {
	keyword: string;
	setSearch: (keyword: string) => void;
}) => {
	const [tempSearchValue, setTempSearchValue] = useState(keyword);
	useEffect(() => {
		setTempSearchValue(keyword);
	}, [keyword]);

	return (
		<SearchBox
			value={tempSearchValue}
			onChange={(val) => {
				setTempSearchValue(val);
			}}
			hasDeleteKey
			hasSearchButton
			onSearch={() => {
				setSearch(tempSearchValue);
			}}
			stackProps={{ width: '100%' }}
		/>
	);
};
